import { countSolutions, solve } from './solver';
import { createRng, randomSeed, shuffle, type Rng } from './rng';
import { rateGrid, type Rating } from './techniques';
import type { Difficulty, Grid, Puzzle } from './types';

/**
 * Erzeugt Rätsel mit garantiert eindeutiger Lösung.
 *
 * Ablauf: vollständiges Gitter zufällig lösen, danach Felder punktsymmetrisch
 * entfernen, solange die Lösung eindeutig bleibt. Anschließend wird das
 * Ergebnis mit menschlichen Lösetechniken bewertet und so lange nachjustiert
 * (weiter ausgraben oder Vorgaben zurückgeben), bis der Aufwand im Zielband
 * des gewünschten Schwierigkeitsgrads liegt.
 */

export interface Band {
  min: number;
  max: number;
}

/**
 * Aufwandsbänder (siehe `rateGrid().score`). Sie sind lückenlos, damit jeder
 * Punktwert genau einem Schwierigkeitsgrad zugeordnet werden kann.
 */
export const BANDS: Record<Difficulty, Band> = {
  easy: { min: 0, max: 42 },
  medium: { min: 43, max: 72 },
  hard: { min: 73, max: 140 },
  expert: { min: 141, max: Number.POSITIVE_INFINITY },
};

/** Anzahl Vorgaben, auf die zunächst ausgegraben wird. */
const TARGET_CLUES: Record<Difficulty, number> = {
  easy: 45,
  medium: 35,
  hard: 29,
  expert: 24,
};

export const DIFFICULTY_LABEL: Record<Difficulty, string> = {
  easy: 'Leicht',
  medium: 'Mittel',
  hard: 'Schwer',
  expert: 'Experte',
};

const ORDER: Difficulty[] = ['easy', 'medium', 'hard', 'expert'];

/** Ordnet einen Aufwandswert einem Schwierigkeitsgrad zu. */
export function classify(score: number): Difficulty {
  for (const difficulty of ORDER) {
    if (score <= BANDS[difficulty].max) return difficulty;
  }
  return 'expert';
}

/** Zufälliges, vollständig gefülltes und gültiges Gitter. */
export function generateSolution(rng: Rng): Grid {
  const solved = solve(new Uint8Array(81), rng);
  if (!solved) throw new Error('Kein vollständiges Gitter gefunden');
  return solved;
}

export function countClues(grid: Grid): number {
  let n = 0;
  for (let i = 0; i < 81; i++) if (grid[i] !== 0) n++;
  return n;
}

/** Entfernt ein Feld (plus Spiegelfeld), wenn die Lösung eindeutig bleibt. */
function tryRemove(puzzle: Grid, index: number, symmetric: boolean): boolean {
  const mirror = 80 - index;
  const pair = symmetric && mirror !== index ? [index, mirror] : [index];
  if (pair.every((p) => puzzle[p] === 0)) return false;

  const backup = pair.map((p) => puzzle[p]);
  for (const p of pair) puzzle[p] = 0;

  if (countSolutions(puzzle, 2) === 1) return true;
  pair.forEach((p, k) => {
    puzzle[p] = backup[k];
  });
  return false;
}

/** Gräbt punktsymmetrisch aus, bis das Ziel an Vorgaben erreicht ist. */
function dig(solution: Grid, targetClues: number, rng: Rng): Grid {
  const puzzle = Uint8Array.from(solution);
  const order = shuffle(
    Array.from({ length: 81 }, (_, i) => i),
    rng,
  );
  let clues = 81;
  for (const i of order) {
    if (clues <= targetClues) break;
    const mirror = 80 - i;
    const removing = (puzzle[i] !== 0 ? 1 : 0) + (mirror !== i && puzzle[mirror] !== 0 ? 1 : 0);
    if (tryRemove(puzzle, i, true)) clues -= removing;
  }
  return puzzle;
}

/**
 * Bringt ein Rätsel in das Zielband: zu leichte werden weiter ausgegraben,
 * zu schwere bekommen einzelne Vorgaben zurück.
 */
function tune(puzzle: Grid, solution: Grid, band: Band, rng: Rng): { grid: Grid; rating: Rating } {
  const work = Uint8Array.from(puzzle);
  let rating = rateGrid(work);

  if (rating.score < band.min) {
    const filled = shuffle(
      Array.from({ length: 81 }, (_, i) => i).filter((i) => work[i] !== 0),
      rng,
    );
    for (const i of filled) {
      if (rating.score >= band.min) break;
      if (work[i] === 0) continue;
      // Ohne Symmetrie, damit auch einzelne Felder noch verschwinden können.
      if (tryRemove(work, i, false)) rating = rateGrid(work);
    }
  }

  if (rating.score > band.max) {
    const holes = shuffle(
      Array.from({ length: 81 }, (_, i) => i).filter((i) => work[i] === 0),
      rng,
    );
    for (const i of holes) {
      if (rating.score <= band.max) break;
      work[i] = solution[i];
      rating = rateGrid(work);
    }
  }

  return { grid: work, rating };
}

export interface GenerateOptions {
  /** Für reproduzierbare Rätsel. */
  seed?: number;
  /** Versuche, bis der Schwierigkeitsgrad exakt getroffen ist. */
  attempts?: number;
}

export function generatePuzzle(difficulty: Difficulty, options: GenerateOptions = {}): Puzzle {
  const seed = options.seed ?? randomSeed();
  const attempts = options.attempts ?? 8;
  const band = BANDS[difficulty];
  const rng = createRng(seed);

  let fallback: Puzzle | null = null;

  for (let attempt = 0; attempt < attempts; attempt++) {
    const solution = generateSolution(rng);
    const dug = dig(solution, TARGET_CLUES[difficulty], rng);
    const { grid, rating } = tune(dug, solution, band, rng);

    const candidate: Puzzle = {
      puzzle: grid,
      solution,
      difficulty: classify(rating.score),
      clues: countClues(grid),
      seed,
    };

    if (candidate.difficulty === difficulty) return candidate;
    if (!fallback || distance(candidate.difficulty, difficulty) < distance(fallback.difficulty, difficulty)) {
      fallback = candidate;
    }
  }

  return fallback!;
}

function distance(a: Difficulty, b: Difficulty): number {
  return Math.abs(ORDER.indexOf(a) - ORDER.indexOf(b));
}
